import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import Facade from '@/Facade'
import { ReelsUtil } from '@/managers/reels/ReelsUtil'
import { IInitResponse, ISpinResponse, SymbolData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { Model } from '@/model/Model'

export class ReelsManager {
    private _errorStopReels: SymbolData[][]
    private _model: Model

    public constructor(model: Model) {
        this._model = model

        GameSignals.init.connect(this.onInitData)
        GameSignals.stopSpin.connect(this.onStopSpin)

        GameSignals.serverError.connect(this.onServerError)
        GameSignals.realityCheck.connect(this.onServerError)
        GameSignals.sessionTimeout.connect(this.onServerError)
        GameSignals.errorConnection.connect(this.onServerError)
        GameSignals.commonError.connect(this.onServerError)
        // Insufficient balance arrives as casinoError → popup while reels already spin.
        GameSignals.casinoError.connect(this.onServerError)
        ;(GameSignals as any).insufficientBalance?.connect?.(this.onServerError)
    }

    private onInitData = (_data: IInitResponse) => {
        const grid = this._model.initialSlotGrid
        const reels: number[][] = ReelsUtil.parseIndexes(grid)
        this._errorStopReels = grid

        if (reels == null) {
            console.error('ReelsManager: no reels data in init response')
            return
        }

        GameSignals.createReels.emit({
            originalGrid: grid,
            reels,
        })
    }

    private onStopSpin = (data: ISpinResponse) => {
        const reels: number[][] = ReelsUtil.parseIndexes(data.slot_grid[0])
        const spinType = String((data as any)?.spin_type || '').toLowerCase()
        const holdAndWinExtra = (data as any)?.hold_and_win_extra || {}
        const isHoldAndWinRespin = spinType === 'respin' || this._model.spinState.isHoldAndWinActive === true
        const scatterReels: number[] = []
        let anticipationIndices: number[] = []
        let isScatterAnticipation: boolean = false

        this._errorStopReels = data.slot_grid[0]

        if (reels == null) {
            console.error('ReelsManager: no reels data in spin response')
            return
        }

        if (!isHoldAndWinRespin) {
            let cumulativeCoinSymbols = 0
            let firstReelIndexWithFiveCoins = -1

            for (let reel = 0; reel < reels.length - 1; reel++) {
                for (const symbolIndex of reels[reel]) {
                    if (symbolIndex === SpecialSymbolType.SCATTER) {
                        scatterReels.push(reel)
                    }

                    if (symbolIndex === SpecialSymbolType.MULTIPLIER) {
                        cumulativeCoinSymbols++
                    }
                }

                if (firstReelIndexWithFiveCoins < 0 && cumulativeCoinSymbols >= 5) {
                    firstReelIndexWithFiveCoins = reel
                }
            }

            // Suspense once at least 2 scatters landed, on the mathematically eligible reels after
            // the second one. Free spins run it too: a third scatter there retriggers the feature,
            // so the reels that can still deliver it deserve the same build-up as in base game.
            if (scatterReels.length >= 2) {
                const eligibleScatterReelIndices = new Set(
                    (Facade.reelsConfig.FREE_SPINS_TRIGGER_ELIGIBLE_REELS || []).map(
                        (reelNumber: number) => reelNumber - 1
                    )
                )

                reels.forEach((reel: number[], index: number) => {
                    if (index > scatterReels[1] && eligibleScatterReelIndices.has(index)) {
                        anticipationIndices.push(index)
                    }
                })

                isScatterAnticipation = anticipationIndices.length > 0
            }

            // Hold&Win suspense: once 5+ coins are landed, apply anticipation to the remaining
            // reels that can still spin. Coins keep triggering Hold&Win from inside free spins,
            // so the threshold is the same there.
            if (!anticipationIndices.length && firstReelIndexWithFiveCoins >= 0) {
                reels.forEach((reel: number[], index: number) => {
                    if (index > firstReelIndexWithFiveCoins) {
                        anticipationIndices.push(index)
                    }
                })
            }
        }

        GameSignals.stoppingReels.emit({
            originalGrid: data.slot_grid[0],
            // Turbo shortens the spin, it does not remove the tension build-up: the reels that can
            // still deliver the feature keep their suspense stop. Only an explicit stop press
            // cancels it, which ReelsContainer handles through its immediate-stop flag.
            anticipationIndices,
            isScatterAnticipation,
            reels,
            isHoldAndWinRespin,
            holdAndWinCoinIndex: Array.isArray(holdAndWinExtra?.coin_index) ? holdAndWinExtra.coin_index : [],
            holdAndWinCoinValues: Array.isArray(holdAndWinExtra?.coin_values) ? holdAndWinExtra.coin_values : [],
            holdAndWinCoinTypes: Array.isArray(holdAndWinExtra?.coin_types) ? holdAndWinExtra.coin_types : [],
            holdAndWinSpinsLeft: Number((data as any)?.respins_left ?? 0),
            holdAndWinSpinsTotal: Number((data as any)?.respins_total ?? 0),
        })

        if (isHoldAndWinRespin) {
            ;(GameSignals as any).holdAndWinStopSpin?.emit({
                originalGrid: data.slot_grid[0],
                anticipationIndices: [],
                isScatterAnticipation: false,
                reels,
                isHoldAndWinRespin,
                holdAndWinCoinIndex: Array.isArray(holdAndWinExtra?.coin_index) ? holdAndWinExtra.coin_index : [],
                holdAndWinCoinValues: Array.isArray(holdAndWinExtra?.coin_values) ? holdAndWinExtra.coin_values : [],
                holdAndWinCoinTypes: Array.isArray(holdAndWinExtra?.coin_types) ? holdAndWinExtra.coin_types : [],
                holdAndWinSpinsLeft: Number((data as any)?.respins_left ?? 0),
                holdAndWinSpinsTotal: Number((data as any)?.respins_total ?? 0),
            })
        }
    }

    private onServerError = () => {
        if (this._errorStopReels == null) {
            return
        }

        const reels: number[][] = ReelsUtil.parseIndexes(this._errorStopReels)

        GameSignals.errorStop.emit({
            originalGrid: this._errorStopReels,
            anticipationIndices: [],
            isScatterAnticipation: false,
            reels,
        })
    }
}
